import { ChevronLeft, ChevronRight } from "lucide-react";

import { PAGE_SIZE_OPTIONS } from "@/lib/paging";

/**
 * Page controls for the assessment tables.
 *
 * One component for all four lists — cases, questions, exams, results —
 * because four hand-rolled pagers is how two of them end up off-by-one
 * and nobody notices until a page is missing rows.
 *
 * Renders NOTHING when everything fits on one page. A pager under a
 * three-row table is noise, and its absence is itself the useful signal
 * that there is no more to see.
 */
export function Pagination({
  page,
  pageSize,
  total,
  onPage,
  onPageSize,
  label = "rows",
}: {
  page: number;
  pageSize: number;
  total: number;
  onPage: (page: number) => void;
  /** Omit to hide the rows-per-page control. */
  onPageSize?: (size: number) => void;
  /** Plural noun for the count, e.g. "cases". */
  label?: string;
}) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  // Hidden when everything fits AND the smallest option would not change
  // that — otherwise a list of 15 offers no way back down to 10.
  if (total <= Math.min(pageSize, PAGE_SIZE_OPTIONS[0])) return null;

  const first = (page - 1) * pageSize + 1;
  const last = Math.min(page * pageSize, total);

  return (
    <div
      data-cy="pagination"
      className="mt-3 flex flex-wrap items-center justify-between gap-3"
    >
      <p className="text-xs text-foreground-muted">
        {first}–{last} of {total} {label}
      </p>
      <div className="flex items-center gap-2">
        {onPageSize && (
          <label className="mr-2 flex items-center gap-1.5 text-xs text-foreground-muted">
            Rows
            <select
              data-cy="page-size"
              value={pageSize}
              onChange={(e) => {
                // Back to page 1: the current page number rarely exists
                // at the new size, and landing on an empty page reads as
                // "the rest is gone".
                onPage(1);
                onPageSize(Number(e.target.value));
              }}
              className="sk-input sk-sm w-auto"
            >
              {PAGE_SIZE_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
        )}
        <button
          type="button"
          data-cy="page-prev"
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
          className="sk-btn sk-sm"
        >
          <ChevronLeft aria-hidden className="h-3.5 w-3.5" />
          Previous
        </button>
        <span className="text-xs tabular-nums text-foreground-muted">
          {page} / {pages}
        </span>
        <button
          type="button"
          data-cy="page-next"
          disabled={page >= pages}
          onClick={() => onPage(page + 1)}
          className="sk-btn sk-sm"
        >
          Next
          <ChevronRight aria-hidden className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
